import * as React from 'react'
import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useT } from '@/components/visitone/visitone-provider'
import { usePageTitle } from '@/hooks/use-page-title'

/** A crumb's label, and the route it opens when it is a page the caller can open. */
export type BreadcrumbItem = string | { label: string; to?: string }

/*
 * PageHeader: identity and the single primary action. The breadcrumb is shown
 * only below level one and reflects the route, never the referrer. An
 * ancestor given a `to` is a router link to that route; one without stays
 * text, because it is not a page of its own (or not one the caller can open).
 * The last crumb is always the current page, as text. Each page has at most
 * one primary button, right-aligned in the header. `status` holds the page's
 * access pills (VIS-329); they follow the title in reading order and wrap
 * below it when the row is too narrow. The title is the page's only `h1` and
 * its document title.
 */
export function PageHeader({
  breadcrumb,
  title,
  summary,
  status,
  action,
}: {
  breadcrumb?: BreadcrumbItem[]
  title: string
  summary?: string
  status?: React.ReactNode
  action?: React.ReactNode
}) {
  const t = useT()
  usePageTitle(title)
  return (
    <header className="flex flex-wrap items-start justify-between gap-3 pb-5">
      <div className="min-w-0">
        {breadcrumb && breadcrumb.length > 1 ? (
          <nav aria-label={t('core.nav.breadcrumb')} className="mb-1">
            <ol className="flex flex-wrap items-center gap-1 text-xs text-fg-muted">
              {breadcrumb.map((item, index) => {
                const { label, to } = typeof item === 'string' ? { label: item, to: undefined } : item
                const last = index === breadcrumb.length - 1
                return (
                  // Labels can repeat (a guest named "Guests"), so crumbs are keyed by position.
                  <li key={index} className="flex items-center gap-1">
                    {index > 0 ? <ChevronRight aria-hidden="true" className="size-3.5" /> : null}
                    {!last && to ? (
                      <Link
                        to={to}
                        className="inline-flex min-h-6 items-center text-fg-muted underline underline-offset-4 hover:text-fg"
                      >
                        {label}
                      </Link>
                    ) : (
                      <span className={last ? 'text-fg' : undefined} aria-current={last ? 'page' : undefined}>
                        {label}
                      </span>
                    )}
                  </li>
                )
              })}
            </ol>
          </nav>
        ) : null}
        {status ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="text-2xl font-semibold text-fg-strong">{title}</h1>
            {status}
          </div>
        ) : (
          <h1 className="text-2xl font-semibold text-fg-strong">{title}</h1>
        )}
        {summary ? <p className="mt-1 text-sm text-fg-muted">{summary}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </header>
  )
}
